// Toast.tsx
"use client";

import React, { useCallback } from "react";
import styles from "./toast.module.css";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  addToast,
  removeToast,
  Toast as ToastInterface,
} from "../../store/features/toast/toastSlice";

type ToastType = "success" | "error" | "warning" | "info";
export type ToastPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

const getIcon = (type: ToastType) => {
  switch (type) {
    case "success":
      return (
        <svg viewBox="0 0 20 20" fill="currentColor" className={styles.icon}>
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      );
    case "error":
      return (
        <svg viewBox="0 0 20 20" fill="currentColor" className={styles.icon}>
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
      );
    case "warning":
      return (
        <svg viewBox="0 0 20 20" fill="currentColor" className={styles.icon}>
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      );
    case "info":
      return (
        <svg viewBox="0 0 20 20" fill="currentColor" className={styles.icon}>
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      );
  }
};

const getPositionStyle = (position: ToastPosition): string => {
  switch (position) {
    case "top-left":
      return styles.topLeft;
    case "top-center":
      return styles.topCenter;
    case "top-right":
      return styles.topRight;
    case "bottom-left":
      return styles.bottomLeft;
    case "bottom-center":
      return styles.bottomCenter;
    case "bottom-right":
      return styles.bottomRight;
  }
};

const getTypeStyle = (type: ToastType): string => {
  switch (type) {
    case "success":
      return styles.success;
    case "error":
      return styles.error;
    case "warning":
      return styles.warning;
    case "info":
      return styles.info;
  }
};

// Custom hook for showing toasts
export function useToast() {
  const dispatch = useAppDispatch();

  const showToast = useCallback(
    (
      message: string,
      type: ToastType = "info",
      position: ToastPosition = "top-right",
      duration: number = 3000
    ) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const newToast: ToastInterface = {
        id,
        message,
        type,
        position,
        duration,
      };

      dispatch(addToast(newToast));

      if (duration > 0) {
        setTimeout(() => {
          dispatch(removeToast(id));
        }, duration);
      }
    },
    [dispatch]
  );

  return {
    showToast,
  };
}

// Toast container component
export const InjectToast: React.FC = () => {
  const { toasts } = useAppSelector((state) => state.toast);
  const dispatch = useAppDispatch();

  const handleRemoveToast = useCallback(
    (id: string) => {
      dispatch(removeToast(id));
    },
    [dispatch]
  );

  // Group toasts by position
  const groupedToasts = toasts.reduce((acc, toast) => {
    if (!acc[toast.position]) {
      acc[toast.position] = [];
    }
    acc[toast.position].push(toast);
    return acc;
  }, {} as Record<ToastPosition, ToastInterface[]>);

  return (
    <>
      {Object.entries(groupedToasts).map(([position, positionToasts]) => (
        <div
          key={position}
          className={`${styles.container} ${getPositionStyle(
            position as ToastPosition
          )}`}
        >
          {positionToasts.map((toast) => (
            <div
              key={toast.id}
              className={`${styles.toast} ${getTypeStyle(toast.type)}`}
            >
              {getIcon(toast.type)}
              <div className={styles.message}>{toast.message}</div>
              <button
                onClick={() => handleRemoveToast(toast.id)}
                className={styles.closeButton}
                aria-label="Close"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <line x1="4" y1="4" x2="12" y2="12" />
                  <line x1="12" y1="4" x2="4" y2="12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      ))}
    </>
  );
};
